import { ArrowDown, ArrowUp, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, cx } from "../../../components/ui";
import { promptUses, renamePrompt, setSlot, SLOTS, slotOf, withSlots, type Definition, type Issue, type Prompt } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
const small = "h-8 rounded-lg border border-line bg-surface px-2 text-sm";
const CODE = /[^A-Za-z0-9_]/g;

/** Built-in lookups a prompt can offer (services/api/src/ja/model.ts BUILTINS). */
const LOOKUPS = [
  {
    value: "paymentSchedules",
    label: "Payment schedules for the HR organization",
  },
  { value: "payPeriodEnds", label: "Pay period end dates for the schedule" },
];

/**
 * The run screen's prompts (docs/phase3-plan.md §1): code, label, kind, options, default, required, lookup and
 * run-key slot. Renaming a code updates every use of it; the run key follows the slots.
 */
export function PromptsEditor({ def, issues, onChange }: { def: Definition; issues: Issue[]; onChange: (d: Definition) => void }) {
  const [selected, setSelected] = useState<string | null>(def.prompts[0]?.code ?? null);
  const prompt = def.prompts.find((p) => p.code === selected);
  const setPrompts = (prompts: Prompt[]) => onChange(withSlots(def, prompts));
  const add = () => {
    let n = def.prompts.length + 1;
    while (def.prompts.some((p) => p.code === `prompt${n}`)) n++;
    setPrompts([
      ...def.prompts,
      {
        code: `prompt${n}`,
        label: "New prompt",
        kind: "text",
        required: false,
      },
    ]);
    setSelected(`prompt${n}`);
  };
  const move = (dir: -1 | 1) => {
    const i = def.prompts.findIndex((p) => p.code === selected);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= def.prompts.length) return;
    const prompts = [...def.prompts];
    [prompts[i], prompts[j]] = [prompts[j], prompts[i]];
    setPrompts(prompts);
  };
  const keyIssues = issues.filter((i) => i.where === "run key");

  return (
    <div className={cx("grid gap-4", prompt ? "lg:grid-cols-[minmax(0,1fr)_28rem]" : "")}>
      <div className="space-y-3">
        <div className="rounded-xl border border-line bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted border-b border-line">
              <tr>
                <th className="px-3 py-2 font-medium w-8">#</th>
                <th className="px-3 py-2 font-medium">Prompt</th>
                <th className="px-3 py-2 font-medium">Kind</th>
                <th className="px-3 py-2 font-medium">Run key</th>
              </tr>
            </thead>
            <tbody>
              {def.prompts.map((p, i) => {
                const bad = issues.some((x) => x.where === `prompt ${p.code}` && x.level === "error");
                return (
                  <tr key={p.code} className={cx("border-b border-line last:border-0 cursor-pointer hover:bg-surface-2", selected === p.code && "bg-accent-soft")} onClick={() => setSelected(p.code)}>
                    <td className="px-3 py-2 text-muted tabular">{i + 1}</td>
                    <td className="px-3 py-2">
                      <button type="button" className="text-left" aria-pressed={selected === p.code} onClick={() => setSelected(p.code)}>
                        <span className={cx("font-medium", bad ? "text-bad" : "text-ink")}>{p.label}</span>
                        {p.required && <span className="text-bad"> *</span>}
                        <span className="block text-xs text-muted font-mono">{p.code}</span>
                      </button>
                    </td>
                    <td className="px-3 py-2 text-ink-2">
                      {p.kind === "select" ? `Choice (${p.options?.length ?? 0})` : p.kind === "date" ? "Date" : "Text"}
                      {p.lookup && <span className="block text-xs text-muted">Looks up in Infor</span>}
                      {p.setBy && <span className="block text-xs text-muted">Set by {p.setBy}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-2">{SLOTS.find((s) => s.value === slotOf(p))?.label ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={add}>
            <Plus className="size-4" aria-hidden /> Add a prompt
          </Button>
          <p className="text-xs text-muted">Run key: {def.runKey.map((k) => def.prompts.find((p) => p.code === k)?.label ?? k).join(" + ") || "none"} + type. One open run per key.</p>
        </div>
        {keyIssues.map((i) => (
          <p key={i.message} className={cx("text-xs", i.level === "error" ? "text-bad" : "text-warn")}>
            {i.message}
          </p>
        ))}
      </div>
      {prompt && (
        <PromptPanel
          key={prompt.code}
          def={def}
          prompt={prompt}
          issues={issues.filter((i) => i.where === `prompt ${prompt.code}`)}
          onChange={(p) => setPrompts(def.prompts.map((x) => (x.code === prompt.code ? p : x)))}
          onRename={(to) => {
            onChange(renamePrompt(def, prompt.code, to));
            setSelected(to);
          }}
          onSlot={(slot) => onChange(setSlot(def, prompt.code, slot))}
          onMove={move}
          onDelete={() => {
            setPrompts(def.prompts.filter((p) => p.code !== prompt.code));
            setSelected(null);
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function PromptPanel({
  def,
  prompt,
  issues,
  onChange,
  onRename,
  onSlot,
  onMove,
  onDelete,
  onClose,
}: {
  def: Definition;
  prompt: Prompt;
  issues: Issue[];
  onChange: (p: Prompt) => void;
  onRename: (to: string) => void;
  onSlot: (slot: string) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState(prompt.code);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => setCode(prompt.code), [prompt.code]);
  const taken = code !== prompt.code && def.prompts.some((p) => p.code === code);
  const commitCode = () => {
    if (code && !taken && code !== prompt.code) onRename(code);
    else setCode(prompt.code);
  };
  const set = (patch: Partial<Prompt>) => onChange({ ...prompt, ...patch });
  const options = prompt.options ?? [];
  const setOption = (i: number, o: { value: string; label: string }) => set({ options: options.map((x, j) => (j === i ? o : x)) });
  const uses = promptUses(def, prompt.code);
  const slot = slotOf(prompt);

  return (
    <aside className="rounded-xl border border-line bg-surface p-4 space-y-4 text-sm" aria-label={`Edit prompt ${prompt.label}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted font-mono">{prompt.legacyCode ? `legacy ${prompt.legacyCode}` : "prompt"}</div>
          <h2 className="font-display font-semibold text-ink text-lg">{prompt.label || "Prompt"}</h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" title="Move up" aria-label="Move up" onClick={() => onMove(-1)}>
            <ArrowUp className="size-4" />
          </Button>
          <Button variant="ghost" title="Move down" aria-label="Move down" onClick={() => onMove(1)}>
            <ArrowDown className="size-4" />
          </Button>
          <Button variant="ghost" title="Delete" aria-label="Delete prompt" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="size-4" />
          </Button>
          <Button variant="ghost" title="Close" aria-label="Close editor" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {issues.map((i) => (
        <p key={i.message} className={cx("text-xs", i.level === "error" ? "text-bad" : "text-warn")}>
          {i.level === "error" ? "Fix: " : "⚠ "}
          {i.message}
        </p>
      ))}
      {confirmDelete && (
        <div className="rounded-lg border border-bad bg-bad-soft p-3 space-y-2">
          <p>{uses.length ? `${prompt.label} is used by ${uses.join(", ")}. Those will need fixing before you can save a version.` : `Remove ${prompt.label}?`}</p>
          <div className="flex gap-2">
            <Button variant="danger" onClick={onDelete}>
              Remove
            </Button>
            <Button onClick={() => setConfirmDelete(false)}>Keep it</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2">
          <span className="text-ink-2 font-medium">Label</span>
          <input className={input} value={prompt.label} onChange={(e) => set({ label: e.target.value })} />
        </label>
        <label className="col-span-2">
          <span className="text-ink-2 font-medium">Code</span>
          <input
            className={cx(input, "font-mono", taken && "border-bad")}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(CODE, ""))}
            onBlur={commitCode}
            onKeyDown={(e) => e.key === "Enter" && commitCode()}
            aria-describedby="prompt-code-help"
          />
          <span id="prompt-code-help" className="text-xs text-muted">
            {taken ? (
              <span className="text-bad">Another prompt has this code.</span>
            ) : uses.length ? (
              `Renaming updates ${uses.length} use${uses.length === 1 ? "" : "s"}: ${uses.join(", ")}.`
            ) : (
              "Not used by any step yet."
            )}
          </span>
        </label>
        <label>
          <span className="text-ink-2 font-medium">Kind</span>
          <select
            className={input}
            value={prompt.kind}
            disabled={slot === "AUTO-DATE"}
            onChange={(e) => {
              const kind = e.target.value as Prompt["kind"];
              set({
                kind,
                ...(kind === "select" && !options.length ? { options: [{ value: "", label: "" }] } : {}),
              });
            }}
          >
            <option value="text">Text</option>
            <option value="date">Date</option>
            <option value="select">Choice</option>
          </select>
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={prompt.required} disabled={slot === "AUTO-DATE"} onChange={(e) => set({ required: e.target.checked })} />
          <span>Required</span>
        </label>
        <label>
          <span className="text-ink-2 font-medium">Default</span>
          {prompt.kind === "select" ? (
            <select className={input} value={prompt.default ?? ""} onChange={(e) => set({ default: e.target.value || undefined })}>
              <option value="">None</option>
              {options
                .filter((o) => o.value)
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label || o.value}
                  </option>
                ))}
            </select>
          ) : (
            <input className={input} type={prompt.kind === "date" ? "date" : "text"} value={prompt.default ?? ""} onChange={(e) => set({ default: e.target.value || undefined })} />
          )}
        </label>
        {prompt.kind === "text" && (
          <label>
            <span className="text-ink-2 font-medium">Longest (characters)</span>
            <input
              className={input}
              type="number"
              min={1}
              max={500}
              value={prompt.maxLength ?? ""}
              onChange={(e) =>
                set({
                  maxLength: e.target.value ? Number(e.target.value) : undefined,
                })
              }
            />
          </label>
        )}
      </div>

      {prompt.kind === "select" && (
        <section className="border-t border-line pt-4 space-y-2">
          <h3 className="text-sm font-semibold text-ink">Options</h3>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={cx(small, "w-28 font-mono")} placeholder="Value" aria-label="Option value" value={o.value} onChange={(e) => setOption(i, { ...o, value: e.target.value })} />
              <input className={cx(small, "flex-1")} placeholder="Label" aria-label="Option label" value={o.label} onChange={(e) => setOption(i, { ...o, label: e.target.value })} />
              <button type="button" className="text-muted hover:text-bad" aria-label={`Remove option ${o.value}`} onClick={() => set({ options: options.filter((_, j) => j !== i) })}>
                <X className="size-4" />
              </button>
            </div>
          ))}
          <button type="button" className="text-xs rounded-full border border-line px-2 py-0.5 hover:bg-surface-2" onClick={() => set({ options: [...options, { value: "", label: "" }] })}>
            + Option
          </button>
        </section>
      )}

      <section className="border-t border-line pt-4 space-y-3">
        <h3 className="text-sm font-semibold text-ink">Run key</h3>
        <label className="block">
          <span className="text-ink-2 font-medium">Slot</span>
          <select className={input} value={slot} onChange={(e) => onSlot(e.target.value)}>
            <option value="">Not part of the run key</option>
            {SLOTS.map((s) => {
              const holder = def.prompts.find((p) => p.code !== prompt.code && p.legacyName === s.value);
              return (
                <option key={s.value} value={s.value}>
                  {s.label}
                  {holder ? ` (now ${holder.label})` : ""}
                </option>
              );
            })}
          </select>
        </label>
        <p className="text-xs text-muted">
          The run key is org units 1–4, then the run date, plus the automation type. Only one run with the same key can be open. The run date is a date, and it names the jobs.
        </p>
      </section>

      <section className="border-t border-line pt-4 space-y-3">
        <h3 className="text-sm font-semibold text-ink">Look up in Infor</h3>
        <select
          className={input}
          value={prompt.lookup?.builtin ?? ""}
          onChange={(e) =>
            set({
              lookup: e.target.value ? { builtin: e.target.value } : undefined,
            })
          }
        >
          <option value="">No lookup (typed)</option>
          {LOOKUPS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        {prompt.lookup?.builtin === "payPeriodEnds" && (
          <label className="block">
            <span className="text-ink-2 font-medium">Also fill the period start into</span>
            <select
              className={input}
              value={prompt.lookup.fills?.find((f) => f.from === "begin")?.prompt ?? ""}
              onChange={(e) =>
                set({
                  lookup: {
                    builtin: "payPeriodEnds",
                    ...(e.target.value ? { fills: [{ prompt: e.target.value, from: "begin" }] } : {}),
                  },
                })
              }
            >
              <option value="">Nothing</option>
              {def.prompts
                .filter((p) => p.code !== prompt.code)
                .map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.label}
                  </option>
                ))}
            </select>
          </label>
        )}
      </section>
      {prompt.setBy && <p className="text-xs text-muted">Filled by {prompt.setBy} during the run.</p>}
    </aside>
  );
}
